/**
 * CallInviteModal
 *
 * In-call sheet for bringing someone in to watch the lesson.
 *
 * The player shares the lesson's own guest link (`/call/invite/{token}`),
 * which the server makes at booking, with Share Link and Copy Link. The
 * rules let only the coach write `callInvites`, so the coach's version keeps
 * the email form: a Cloud Function picks the invite up and sends the email,
 * whose link is https://app.doublebagel.net/call/{bookingId}?token={invite.token}.
 */

import { useEffect, useState } from 'react';
import { GUEST_JOIN } from '../config/featureFlags';
import ButtonSpinner from './ButtonSpinner';
import { BTN_PRIMARY } from './buttonStyles';
import { busyStyle } from '../utils/busyStyle';
import { createCallInvite, getInvitesForBooking } from '../services/callInvites';
import { getLiveLessonLink } from '../services/liveV2';
import { emailedInvites, inviteFootnote, lessonLinkToken } from '../utils/inviteSheet';
import { BTN_ON_CANVAS } from './live/RoomScreens';
import type { CallInvite } from '../types/liveCall';

const APP_DOMAIN = 'https://app.doublebagel.net';

interface CallInviteModalProps {
  bookingId: string;
  playerId: string;
  coachId: string;
  /** The More menu's label for it, "Invite Someone" or "Invite a Parent" (recordingNotice). */
  title: string;
  /** The player is under 18. */
  minor: boolean;
  /** The coach, whom the rules let write an invite: the email form. Otherwise the lesson's link. */
  canEmail: boolean;
  /** The booking's invites, read by the room when it opened, so the lesson's link shows at once. */
  prefetched?: CallInvite[] | null;
  onClose: () => void;
}

export function CallInviteModal({ bookingId, playerId, coachId, title, minor, canEmail, prefetched, onClose }: CallInviteModalProps) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [existing, setExisting] = useState<CallInvite[]>(() => emailedInvites(prefetched ?? []));

  // Invites already sent for this booking: the emailed ones. The lesson's
  // own link (the booking's callInvite, made by the server at booking) has
  // no address. Shown at once from the room's read, and read again here; a
  // lesson booked before that gets one from the server (getLiveLessonLink).
  const [linkToken, setLinkToken] = useState<string | null>(() => (prefetched ? lessonLinkToken(prefetched) : null));
  const [linkChecked, setLinkChecked] = useState(() => !!prefetched);
  useEffect(() => {
    getInvitesForBooking(bookingId, coachId, playerId)
      .then(async (all) => {
        setExisting(emailedInvites(all));
        const own = lessonLinkToken(all);
        setLinkToken(own ?? (await getLiveLessonLink({ bookingId })).token);
      })
      .catch(() => { /* best-effort */ })
      .finally(() => setLinkChecked(true));
  }, [bookingId, coachId, playerId]);
  const link = linkToken ? `${window.location.origin}/call/invite/${linkToken}` : null;
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  const [copied, setCopied] = useState(false);
  const copyLink = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Could not copy the link. Please try again.');
    }
  };
  const shareLink = () => {
    if (!link) return;
    navigator.share({ title, url: link }).catch(() => { /* the share sheet was dismissed */ });
  };

  const handleSend = async () => {
    const trimEmail = email.trim().toLowerCase();
    if (!trimEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimEmail)) {
      setError('Enter a valid email address.');
      return;
    }
    if (existing.some((inv) => inv.invitedEmail === trimEmail)) {
      setError('An invite was already sent to this address.');
      return;
    }
    setSending(true);
    setError(null);
    try {
      const invite = await createCallInvite(bookingId, coachId, playerId, trimEmail, name.trim() || undefined);
      setExisting((prev) => [...prev, invite]);
      setSent(true);
      setEmail('');
      setName('');
      setTimeout(() => setSent(false), 3000);
    } catch {
      setError('Could not send the invite. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    /* Full-screen overlay that sits above the call UI (z-60) */
    <div
      className="fixed inset-0 z-60 flex items-end justify-center"
      style={{ background: 'rgba(0,0,0,0.6)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* A sheet on the call canvas (LC-1): top radius 34, no border */}
      <div
        className="flex w-full max-w-lg flex-col rounded-t-[34px] bg-call-canvas"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}
      >
        {/* Nav bar (SH-8): Cancel on the leading edge, the title centred */}
        <div className="relative flex h-14 items-center justify-center px-4">
          <button
            type="button"
            onClick={onClose}
            className="ios-press absolute left-2 flex h-11 items-center rounded-full px-3 text-[17px] leading-[22px] text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
          >
            Cancel
          </button>
          <h2 className="text-[17px] leading-[22px] font-semibold text-white">
            {title}
          </h2>
        </div>

        {!GUEST_JOIN ? (
          /* Guests by link wait on the wrapper's three-peer room (config/featureFlags). */
          <div className="px-4 pb-4" data-invites-soon>
            <div className="flex min-h-[52px] items-center rounded-[12px] bg-call-tile px-4 py-3">
              <span className="text-[17px] leading-[22px] text-white">Invites open soon</span>
            </div>
          </div>
        ) : !canEmail ? (
          /* The lesson's link, to share or copy */
          <div className="px-4 flex flex-col">
            <div className="flex min-h-[52px] items-center rounded-[12px] bg-call-tile px-4 py-3" data-invite-link>
              {link ? (
                <span className="min-w-0 break-all text-[15px] leading-[20px] text-white/60 select-all">{link}</span>
              ) : linkChecked ? (
                <span className="text-[15px] leading-[20px] text-white/60">This lesson's link isn't ready yet.</span>
              ) : (
                <ButtonSpinner size={16} />
              )}
            </div>
            <p className="mt-2 px-4 text-[13px] leading-[18px] text-white/60">
              {inviteFootnote(minor, 'link')}
            </p>
            {error && (
              <p className="mt-3 px-4 text-[13px] leading-[18px] text-white/85">{error}</p>
            )}
            {canShare && (
              <button type="button" onClick={shareLink} disabled={!link} className={`${BTN_PRIMARY} mt-4 w-full`}>
                Share Link
              </button>
            )}
            <button
              type="button"
              onClick={() => { void copyLink(); }}
              disabled={!link}
              className={`${canShare ? BTN_ON_CANVAS : BTN_PRIMARY} ${canShare ? 'mt-2' : 'mt-4'} w-full`}
              style={copied ? { background: 'var(--editor-success)', color: '#fff' } : undefined}
            >
              {copied ? 'Copied' : 'Copy Link'}
            </button>
          </div>
        ) : (
        /* Form */
        <div className="px-4 flex flex-col">
          <div className="overflow-hidden rounded-[12px] bg-call-tile">
            <input
              type="text"
              placeholder="Name (optional)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="block h-[52px] w-full px-4 bg-transparent text-white text-[17px] leading-[20px] focus:outline-none placeholder:text-white/40"
            />
            <div className="ml-4 h-px bg-white/10" />
            <input
              type="email"
              inputMode="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setError(null); }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void handleSend(); } }}
              className="block h-[52px] w-full px-4 bg-transparent text-white text-[17px] leading-[20px] focus:outline-none placeholder:text-white/40"
              autoComplete="email"
            />
          </div>

          {/* 8.2: the explanation is a footnote under its section */}
          <p className="mt-2 px-4 text-[13px] leading-[18px] text-white/60">
            {inviteFootnote(minor, 'email')}
          </p>

          {error && (
            <p className="mt-3 px-4 text-[13px] leading-[18px] text-white/85">{error}</p>
          )}

          <button
            type="button"
            onClick={handleSend}
            disabled={sending || !email.trim()}
            aria-busy={sending}
            className={`${BTN_PRIMARY} mt-4 w-full`}
            style={{ ...busyStyle(sending), ...(sent ? { background: 'var(--editor-success)' } : {}) }}
          >
            {/* Busy: the spinner in the label's place (8.3b); a brief success in green */}
            {sending ? <ButtonSpinner /> : sent ? 'Invite Sent' : 'Send Invite'}
          </button>
        </div>
        )}

        {/* Previously sent invites */}
        {GUEST_JOIN && existing.length > 0 && (
          <div className="px-4 pt-6">
            <p className="mb-2 px-4 text-[13px] leading-[18px] text-white/60">
              Invited
            </p>
            <div className="overflow-hidden rounded-[12px] bg-call-tile">
              {existing.map((inv, index) => (
                <div key={inv.id} className="pl-4">
                  <div className={`flex min-h-[52px] items-center gap-3 py-2 pr-4 ${index === 0 ? '' : 'border-t border-white/10'}`}>
                    <div className="flex min-w-0 flex-1 flex-col">
                      {inv.invitedName && (
                        <span className="[overflow-wrap:anywhere] text-white text-[17px] leading-[22px]">
                          {inv.invitedName}
                        </span>
                      )}
                      <span className="[overflow-wrap:anywhere] text-white/60 text-[15px] leading-[20px]">
                        {inv.invitedEmail}
                      </span>
                    </div>
                    <span
                      className="flex-shrink-0 text-[15px] leading-[20px]"
                      style={{ color: inv.status === 'joined' ? 'var(--editor-success)' : 'rgba(255,255,255,0.6)' }}
                    >
                      {inv.status === 'joined' ? 'In call' : inv.status === 'sent' ? 'Sent' : 'Pending'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export { APP_DOMAIN };
